import { useQueries } from '@tanstack/react-query';
import * as React from 'react';

import * as api from '@gitbook/api-client';

import { apiQuery, useLoggedInUser } from '../../state';
import { useAgentEnabled } from '../Agent';
import { useSpaceMergeRules } from '../MergeRules';

export type ChangeRequestPendingRequestedReviewer = {
    kind: 'user';
    userId: string;
};

export type ChangeRequestReviewersData = {
    reviews: api.ChangeRequestReview[];
    requestedReviewers: api.ChangeRequestRequestedReviewer[];
    suggestions: ChangeRequestPendingRequestedReviewer[];
    existingReviewers: Set<string>;
    isLoading: boolean;
};

/**
 * Hook to fetch and compute reviewers data for a change request.
 */
export function useChangeRequestReviewers(
    space: api.Space,
    changeRequest: api.ChangeRequest,
    pendingRequestedReviewers: ChangeRequestPendingRequestedReviewer[] = []
): ChangeRequestReviewersData {
    const withAgentEnabled = useAgentEnabled();

    const [
        { data: { items: reviews } = { items: [] }, isLoading: isLoadingAllReviews },
        {
            data: { items: requestedReviewers } = { items: [] },
            isLoading: isLoadingRequestedReviewers,
        },
    ] = useQueries({
        queries: [
            apiQuery('getReviewsByChangeRequestId', {
                spaceId: space.id,
                changeRequestId: changeRequest.id,
                limit: 10, // Fetch the top 10 reviews, avoid pagination for now whilst we observe usage
                outdated: false,
            }),
            apiQuery('getRequestedReviewersByChangeRequestId', {
                spaceId: space.id,
                changeRequestId: changeRequest.id,
                limit: 10, // Fetch the top 10 requests, avoid pagination for now whilst we observe usage
            }),
        ],
    });
    const isLoading = isLoadingAllReviews || isLoadingRequestedReviewers;

    //
    // Build the list of suggestions
    //
    const mergeRules = useSpaceMergeRules(space);
    const currentUser = useLoggedInUser();
    const { suggestions, existingReviewers } = React.useMemo(() => {
        const rawSuggestions: ChangeRequestPendingRequestedReviewer[] = [];

        // If the merge rules are set, add the specific reviewers to the list
        if (mergeRules.type === 'rules') {
            mergeRules.rules.forEach((ruleSet) => {
                if (
                    ruleSet.rule === 'require_specific_reviewers' ||
                    ruleSet.rule === 'require_one_of_specific_reviewers'
                ) {
                    ruleSet.users.forEach((userId) => {
                        if (userId === currentUser.uid) {
                            return;
                        }

                        rawSuggestions.push({ kind: 'user', userId });
                    });
                }
            });
        }

        // If the docs agents feature is enabled, add the Gitbook agent to the list
        if (withAgentEnabled) {
            rawSuggestions.push({ kind: 'user', userId: api.GitBookUsers.GitbookAgent });
        }

        // Remove from the list the reviewers that are already configured.
        const existingReviewers = new Set([
            ...reviews.map((review) => review.reviewer.id),
            ...requestedReviewers.map((reviewer) =>
                reviewer.kind === 'user' ? reviewer.user.id : reviewer.team.id
            ),
            ...(pendingRequestedReviewers
                ? pendingRequestedReviewers.map((reviewer) => reviewer.userId)
                : []),
        ]);
        const suggestions = rawSuggestions.filter((suggestion) => {
            return !existingReviewers.has(suggestion.userId);
        });

        return { suggestions, existingReviewers };
    }, [
        withAgentEnabled,
        reviews,
        requestedReviewers,
        pendingRequestedReviewers,
        mergeRules,
        currentUser.uid,
    ]);

    return {
        reviews,
        requestedReviewers,
        suggestions,
        existingReviewers,
        isLoading,
    };
}
